import { CronofyElement } from "@/Components/Scheduling/Cronofy/cronofyElement";
import { Client, Coach, None, PageProps } from "@/types/page";
import {
    Appointment,
    APPOINTMENT_DURATION_MINUTES,
} from "@/types/scheduling/appointment";
import { CronofyDatePickerEvent } from "@/types/scheduling/cronofy";
import { CronofyAccount } from "@/types/scheduling/cronofyAccount";
import { usePage } from "@inertiajs/react";
import * as Sentry from "@sentry/react";
import { DateTimePicker } from "cronofy-elements";
import {
    addHours,
    addWeeks,
    endOfDay,
    endOfWeek,
    formatISO,
    isAfter,
    isSameDay,
    isSameWeek,
} from "date-fns";
import {
    forwardRef,
    RefObject,
    useEffect,
    useId,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import "./DateTimePickerStyles.css";

type DateTimePickerWrapperProps = {
    elementToken: string;
    callback?: (notification: CronofyDatePickerEvent) => void;
    refCallback?: RefObject<((e: CronofyDatePickerEvent) => void) | null>;
    cronofyAccounts: CronofyAccount[];
    coachOverride: boolean;
    viewingAsClient?: boolean;
    disableSelection?: boolean;
    confirmButtonOverride?: string;
    timezonesToShow?: string[];
    selectedDate?: string | null; // YYYY-MM-DD
    coachId?: number;
    isRescheduling: boolean;
    reschedulingAppointmentId?: number;
    hasExtraSessionsWithCost: boolean;
    coachScheduler: CoachScheduler;
    lastAppointment: Appointment | null;
    upcomingAppointments: Appointment[] | null;
    startPeriodOverride?: string | null;
    calendarBlockingEventIds: string[];
};

type Member = {
    sub: string;
    managed_availability: string;
    calendar_ids?: string[];
};

export type CoachScheduler = {
    minimumNoticeInHours: MinimumNoticeIncrementInHoursData;
    allowHalfHourBooking: boolean;
    bufferDurationMinutes: number;
};

export type MinimumNoticeIncrementInHoursData = 1 | 2 | 4 | 8 | 12 | 24;

const WEEKS_OUT_TO_SHOW = 12;

const DateTimePickerWrapper = forwardRef(
    (props: DateTimePickerWrapperProps, ref) => {
        const user = usePage<PageProps<None, Client | Coach>>().props.user;

        const [element, setElement] = useState<CronofyElement>(null);

        const uniqueElemId =
            `cronofy-date-time-picker-` + useRef(useId()).current;

        const allowedThisWeek = () => {
            if (
                props.lastAppointment == null &&
                props.upcomingAppointments == null
            ) {
                return true;
            }

            const today = new Date();

            if (props.lastAppointment?.start_at) {
                const lastAppointmentStart = new Date(
                    props.lastAppointment.start_at,
                );
                if (
                    isSameWeek(today, lastAppointmentStart, {
                        weekStartsOn: 1,
                    })
                ) {
                    return false; // if the last appointment was this week, they can't book another one this week
                }
            }

            if (props.upcomingAppointments?.length) {
                return !props.upcomingAppointments.some((appointment) => {
                    return isSameWeek(new Date(appointment.start_at), today, {
                        weekStartsOn: 1,
                    });
                });
            }

            return true; // if no last appointment and no upcoming appointments, they can book this week
        };

        const colors = {
            black: "#040707",
            white: "#FFFFFF",
            border: "#CDD0CC",
            red: "#DC1818",
            lightGray: "#F1F1F1",
        };

        const calculateStartPeriod = (): Date => {
            // If a start period override is provided, use it. and exit logic.
            if (props.startPeriodOverride) {
                return new Date(props.startPeriodOverride);
            }

            if (user.type === "coach" && !props.viewingAsClient) {
                return new Date();
            }

            const now = new Date();
            const minimumNoticeDate = addHours(
                now,
                props.coachScheduler.minimumNoticeInHours,
            );

            if (props.isRescheduling) {
                return minimumNoticeDate;
            }

            if (props.hasExtraSessionsWithCost) {
                // if they had a session today, they can have another tomorrow (still respecting the minimum notice time)
                // else, the minimum notice time for the coach applies
                if (
                    props.lastAppointment?.start_at &&
                    isSameDay(new Date(props.lastAppointment.start_at), now)
                ) {
                    const tomorrow = endOfDay(now);
                    return minimumNoticeDate > tomorrow
                        ? minimumNoticeDate
                        : tomorrow;
                }
                return minimumNoticeDate;
            } else {
                // weekly sessions clients:
                // if they had or have a session this week, this week is blocked
                if (allowedThisWeek()) {
                    return minimumNoticeDate;
                } else {
                    const startOfNextWeek = endOfWeek(now, { weekStartsOn: 1 });
                    return minimumNoticeDate > startOfNextWeek
                        ? minimumNoticeDate
                        : startOfNextWeek;
                }
            }
        };

        const startPeriodDate = calculateStartPeriod();
        const startPeriod = formatISO(startPeriodDate);
        let endPeriod = formatISO(
            endOfDay(addWeeks(startPeriodDate, WEEKS_OUT_TO_SHOW)),
        );
        if (props.selectedDate) {
            if (isAfter(new Date(props.selectedDate), new Date(endPeriod))) {
                endPeriod = formatISO(
                    endOfDay(
                        addWeeks(
                            new Date(props.selectedDate),
                            WEEKS_OUT_TO_SHOW,
                        ),
                    ),
                );
            }
        }

        useImperativeHandle(ref, () => ({
            resetElement: () => {
                element?.refresh();
            },
        }));

        const baseCallback = async (e: CronofyDatePickerEvent) => {
            if (
                e.notification.type === "slot_selected" &&
                user.type === "client" &&
                user.has_payment_setup === false &&
                props.coachId
            ) {
                try {
                    await window.axios.post(
                        route("second-bite.cache-appointment", {
                            coach: props.coachId,
                        }),
                        {
                            start_at: e.notification.slot.start,
                            end_at: e.notification.slot.end,
                        },
                    );
                } catch (error) {
                    Sentry.captureException(error);
                }
            }
            if (props.callback) {
                props.callback(e);
            }
            if (props.refCallback) {
                props.refCallback.current?.(e);
            }
        };

        useEffect(() => {
            if (!element) {
                let excludedEvents: { event_id: string }[] = [];
                let members: Member[];
                if (user.type === "coach" && props.coachOverride) {
                    // coach override view
                    // block only other sessions inside our application
                    members = props.cronofyAccounts
                        .filter((account) => account.is_application_calendar)
                        .map((account) => {
                            // coach view
                            return {
                                sub: account.sub,
                                // for coaches, we do not want to use managed availability
                                // because they can override their own rules
                                managed_availability: "false",
                            };
                        });
                    if (props.calendarBlockingEventIds.length > 0) {
                        excludedEvents = props.calendarBlockingEventIds.map(
                            (id) => ({ event_id: id }),
                        );
                    }
                } else {
                    // standard coach and client view
                    // we block all events from
                    // all the coaches calendars and blocked times inside the app
                    members = props.cronofyAccounts.map((account) => {
                        const member: Member = {
                            sub: account.sub,
                            // accounts that are application calendars
                            // should use 'managed availability', so we can control
                            // the start and end of days (or any other logic we want)
                            // non application calendars are coach's external calendars
                            // and are just used to block events
                            managed_availability:
                                account.is_application_calendar
                                    ? "true"
                                    : "false",
                        };

                        if (
                            !account.is_application_calendar &&
                            account.busy_free_calendar_ids !== null
                        ) {
                            member.calendar_ids =
                                account.busy_free_calendar_ids;
                        }

                        return member;
                    });
                }

                if (props.reschedulingAppointmentId) {
                    excludedEvents.push({
                        event_id: String(props.reschedulingAppointmentId),
                    });
                }

                const params = {
                    target_id: uniqueElemId,
                    element_token: props.elementToken,
                    availability_query: {
                        excluded_events: excludedEvents,
                        participants: [
                            {
                                required: "all",
                                members: members,
                            },
                        ],
                        required_duration: {
                            minutes: APPOINTMENT_DURATION_MINUTES,
                        },
                        ...(!props.coachOverride &&
                            props.coachScheduler.bufferDurationMinutes > 0 && {
                                buffer: {
                                    before: {
                                        minutes:
                                            props.coachScheduler
                                                .bufferDurationMinutes,
                                    },
                                    after: {
                                        minutes:
                                            props.coachScheduler
                                                .bufferDurationMinutes,
                                    },
                                },
                            }),
                        query_periods: [
                            {
                                start: startPeriod,
                                end: endPeriod,
                            },
                        ],
                        start_interval: {
                            minutes: props.coachOverride
                                ? 30
                                : props.coachScheduler.allowHalfHourBooking
                                  ? 30
                                  : 60,
                        },
                    },
                    callback: baseCallback,
                    config: {
                        selected_date: props.selectedDate,
                        week_start_day: "monday",
                        ...(props.timezonesToShow && {
                            tz_list: props.timezonesToShow,
                        }),
                    },
                    styles: {
                        colors: {
                            black: colors.black,
                            white: colors.white,
                            neutralDark: colors.border, // Outline color
                            neutral: colors.border,
                            neutralLight: colors.white,
                            button: colors.white,
                            buttonConfirm: colors.red,
                            buttonConfirmText: colors.white,
                            buttonHover: colors.lightGray,
                            buttonText: colors.black,
                            buttonTextHover: colors.black,
                            buttonActive: colors.red,
                            buttonActiveText: colors.white,
                        },
                    },
                    ...(props.confirmButtonOverride && {
                        translations: {
                            en: {
                                date_time_picker: {
                                    confirm: props.confirmButtonOverride,
                                },
                            },
                        },
                    }),
                };

                const el = DateTimePicker(params);
                setElement(el);
            }
        }, []);

        return (
            <div
                className={
                    props.disableSelection ? "disabled-schedule-selection" : ""
                }
                id={uniqueElemId}
                data-testid="disabled-version-schedule-selection"
            />
        );
    },
);

export default DateTimePickerWrapper;
